/**
 * WebSocket Service for Real-time Communication
 * Handles all WebSocket connections and event management
 */

import { io, Socket } from 'socket.io-client';

interface SimulationEvent {
  simulation_id: number;
  name: string;
  status: 'started' | 'completed' | 'failed';
  error?: string;
  timestamp: string;
}

interface ChatEvent {
  chat_id: string;
  chat_type: 'persona' | 'simulation' | 'group';
  user_id: number;
  message?: string;
  response?: string;
  error?: string;
  timestamp: string;
  simulation_id?: number;
  persona_id?: number;
  persona_ids?: number[];
  persona_count?: number;
  tab_id?: string; // Tab identifier for isolating chat sessions
  chat_history?: any[]; // Added for polling response
}

interface PdfEvent {
  pdf_job_id: string;
  user_id: number;
  simulation_id: number;
  pdf_url?: string;
  simulation_name?: string;
  error?: string;
  timestamp: string;
}

interface QuestionParsingEvent {
  task_id: string;
  user_id: number;
  status: 'started' | 'progress' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  current_step?: string;
  parsed_questions?: any[];
  questions_count?: number;
  error?: string;
  timestamp: string;
}

interface UsageUpdateEvent {
  user_id: number;
  usage_type: 'simulations' | 'questions_parsed' | 'group_chat' | 'simulation_chat';
  monthly_simulations_used?: number;
  simulations_limit?: number;
  monthly_questions_used?: number;
  monthly_group_chats_used?: number;
  group_chats_limit?: number;
  monthly_simulation_chats_used?: number;
  simulation_chats_limit?: number;
  timestamp: string;
}

interface EventCallbacks {
  onSimulationStarted?: (data: SimulationEvent) => void;
  onSimulationCompleted?: (data: SimulationEvent) => void;
  onSimulationFailed?: (data: SimulationEvent) => void;
  onConnectionStatus?: (connected: boolean) => void;
  // Chat events
  onChatMessageReceived?: (data: ChatEvent) => void;
  onChatResponseStarted?: (data: ChatEvent) => void;
  onChatResponseCompleted?: (data: ChatEvent) => void;
  onChatResponseFailed?: (data: ChatEvent) => void;
  // Persona consolidation events
  onPersonaConsolidationStarted?: (data: ChatEvent) => void;
  onPersonaConsolidationCompleted?: (data: ChatEvent) => void;
  // PDF events
  onPdfGenerationStarted?: (data: PdfEvent) => void;
  onPdfGenerationCompleted?: (data: PdfEvent) => void;
  onPdfGenerationFailed?: (data: PdfEvent) => void;
  // Question parsing events
  onQuestionParsingStarted?: (data: QuestionParsingEvent) => void;
  onQuestionParsingProgress?: (data: QuestionParsingEvent) => void;
  onQuestionParsingCompleted?: (data: QuestionParsingEvent) => void;
  onQuestionParsingFailed?: (data: QuestionParsingEvent) => void;
  onQuestionParsingCancelled?: (data: QuestionParsingEvent) => void;
  // Usage update events
  onUsageUpdated?: (data: UsageUpdateEvent) => void;
}

// Polling status interface
interface PollingStatus {
  chat_id: string;
  status: 'pending' | 'processing' | 'consolidating_personas' | 'completed' | 'failed';
  details?: string;
  response?: string;
  chat_history?: any[];
  error?: string;
  timestamp: string;
}

class SocketService {
  private socket: Socket | null = null;
  private callbacks: EventCallbacks = {};
  private subscriberCallbacks: Map<string, EventCallbacks> = new Map(); // Use Map for O(1) removal
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private isConnecting = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private baseReconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  
  // Polling-related properties
  private usePolling: boolean;
  private pollingIntervals: Map<string, ReturnType<typeof setInterval>> = new Map();
  private activeChatIds: Set<string> = new Set();
  private chatTabIds: Map<string, string> = new Map(); // Store tab IDs for each chat
  private activeSimulationIds: Set<number> = new Set(); // Track active simulation polling
  private activeQuestionParsingTaskIds: Set<string> = new Set(); // Track active question parsing polling

  constructor() {
    // Check if we should use polling (only if explicitly enabled)
    // FastAPI now supports Socket.IO, so we use WebSocket mode by default
    this.usePolling = import.meta.env.VITE_USE_POLLING === 'true';
    
    if (this.usePolling) {
      console.log('🔄 Using polling mode for real-time updates');
    } else {
      console.log('🔌 Using WebSocket mode for real-time updates (Socket.IO)');
    }
  }

  /**
   * Get singleton instance of the service
   */
  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  private static instance: SocketService;

  /**
   * Connect to WebSocket server (or initialize polling)
   */
  public connect(): Promise<Socket | boolean> {
    return new Promise((resolve, reject) => {
      // If using polling, just return success
      if (this.usePolling) {
        this.callbacks.onConnectionStatus?.(true);
        resolve(true);
        return;
      }

      // Original WebSocket connection logic
      if (this.socket?.connected) {
        resolve(this.socket);
        return;
      }

      if (this.isConnecting) {
        // Wait for existing connection attempt
        const checkConnection = () => {
          if (this.socket?.connected) {
            resolve(this.socket);
          } else if (!this.isConnecting) {
            reject(new Error('Connection failed'));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
        return;
      }

      this.isConnecting = true;

      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        
        // Get user_id from localStorage or session storage
        const userDataStr = localStorage.getItem('user') || sessionStorage.getItem('user');
        let userId = null;
        if (userDataStr) {
          try {
            const userData = JSON.parse(userDataStr);
            userId = userData.id || userData.user_id;
          } catch (e) {
            console.warn('Failed to parse user data for Socket.IO auth');
          }
        }
        
        this.socket = io(apiUrl, {
          withCredentials: true,
          auth: userId ? { user_id: userId } : undefined, // Pass user_id for room subscription
          transports: ['websocket'], // Websocket only
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          upgrade: false,
          forceNew: false
        });

        this.socket.on('connect', () => {
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.callbacks.onConnectionStatus?.(true);
          resolve(this.socket!);
        });

        this.socket.on('connect_error', (error) => {
          console.error('❌ WebSocket connection error:', error);
          this.isConnecting = false;
          this.callbacks.onConnectionStatus?.(false);
          reject(error);
        });

        this.socket.on('disconnect', (reason) => {
          console.log(`🔌 WebSocket disconnected: ${reason}`);
          this.callbacks.onConnectionStatus?.(false);
          
          // Notify all subscribers about disconnect
          this.subscriberCallbacks.forEach((callback) => {
            callback.onConnectionStatus?.(false);
          });
          
          // Auto-reconnect for certain disconnect reasons
          // 'io server disconnect' means the server forcefully disconnected
          // 'transport close' means network issue or server down
          // 'ping timeout' means server not responding
          if (reason === 'transport close' || reason === 'ping timeout') {
            console.log('🔄 Attempting to reconnect due to network issue...');
            this.manualReconnect();
          }
        });

        this.socket.on('reconnect', (attemptNumber) => {
          console.log(`✅ WebSocket reconnected after ${attemptNumber} attempts`);
          this.reconnectAttempts = 0;
          this.callbacks.onConnectionStatus?.(true);
          
          // Notify all subscribers about reconnect
          this.subscriberCallbacks.forEach((callback) => {
            callback.onConnectionStatus?.(true);
          });
        });

        this.socket.on('reconnect_failed', () => {
          console.error('❌ WebSocket built-in reconnection failed, trying manual reconnection...');
          this.callbacks.onConnectionStatus?.(false);
          
          // Try manual reconnection with exponential backoff
          this.manualReconnect();
        });

        // Setup event listeners
        this.setupEventListeners();

      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * Manual reconnection with exponential backoff
   * Called when built-in reconnection fails or on certain disconnect reasons
   */
  private manualReconnect(): void {
    // Don't reconnect if using polling mode
    if (this.usePolling) return;
    
    // Don't attempt if already connecting or connected
    if (this.isConnecting || this.socket?.connected) return;
    
    // Check if we've exceeded max attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`❌ Max reconnection attempts (${this.maxReconnectAttempts}) reached. Giving up.`);
      return;
    }
    
    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Calculate delay with exponential backoff + jitter
    const exponentialDelay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );
    // Add random jitter (0-1000ms) to prevent thundering herd
    const jitter = Math.random() * 1000;
    const delay = exponentialDelay + jitter;
    
    this.reconnectAttempts++;
    
    console.log(`🔄 Manual reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${Math.round(delay)}ms...`);
    
    this.reconnectTimer = setTimeout(async () => {
      try {
        // Clear old socket before reconnecting
        if (this.socket) {
          this.socket.removeAllListeners();
          this.socket.disconnect();
          this.socket = null;
        }
        
        await this.connect();
        console.log('✅ Manual reconnection successful');
        this.reconnectAttempts = 0;
        
      } catch (error) {
        console.error('❌ Manual reconnection attempt failed:', error);
        // Try again
        this.manualReconnect();
      }
    }, delay);
  }

  /**
   * Stop any pending reconnection attempts
   */
  public stopReconnection(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
  }

  /**
   * Start polling for a specific chat
   */
  public startPollingForChat(chatId: string, tabId?: string): void {
    console.log(`🚀 startPollingForChat called - chatId: ${chatId}, tabId: ${tabId}, usePolling: ${this.usePolling}`);
    if (!this.usePolling) {
      console.log(`⚠️ Not in polling mode, returning`);
      return;
    }
    
    // Avoid duplicate polling
    if (this.pollingIntervals.has(chatId)) {
      console.log(`⚠️ Already polling for chat: ${chatId}`);
      return;
    }
    
    console.log(`🔄 Starting polling for chat: ${chatId}`);
    this.activeChatIds.add(chatId);
    
    // Store tab ID for this chat
    if (tabId) {
      this.chatTabIds.set(chatId, tabId);
    }
    
    // Immediately trigger "started" event to update UI
    const startedEvent: ChatEvent = {
      chat_id: chatId,
      chat_type: this.determineChatType(chatId),
      user_id: 0, // Will be filled from context
      timestamp: new Date().toISOString(),
      tab_id: tabId, // Include tab ID in event
    };
    console.log(`📤 Triggering chat response started event:`, startedEvent);
    this.triggerChatResponseStarted(startedEvent);
    
    // Poll immediately
    this.pollChatStatus(chatId);
    
    // Set up interval for continued polling
    const interval = setInterval(() => {
      this.pollChatStatus(chatId);
    }, 1500); // Poll every 1.5 seconds
    
    this.pollingIntervals.set(chatId, interval);
  }

  /**
   * Stop polling for a specific chat
   */
  public stopPollingForChat(chatId: string): void {
    if (!this.usePolling) return;
    
    const interval = this.pollingIntervals.get(chatId);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(chatId);
    }
    this.activeChatIds.delete(chatId);
    // Clean up the started flag too
    this.activeChatIds.delete(`${chatId}_started`);
    // Clean up tab ID
    this.chatTabIds.delete(chatId);
    console.log(`🛑 Stopped polling for chat: ${chatId}`);
  }

  /**
   * Poll the status of a specific chat
   */
  private async pollChatStatus(chatId: string): Promise<void> {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/task/poll/${chatId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to poll status: ${response.status}`);
      }

      const data: PollingStatus = await response.json();
      
      // Convert polling status to chat event format
      const chatEvent: ChatEvent = {
        chat_id: chatId,
        chat_type: this.determineChatType(chatId),
        user_id: 0, // Will be filled from context
        response: data.response,
        chat_history: data.chat_history,
        error: data.error || data.details,
        timestamp: data.timestamp,
        tab_id: this.chatTabIds.get(chatId), // Include tab ID if available
      };

      // Trigger appropriate callbacks based on status
      switch (data.status) {
        case 'consolidating_personas':
          // Trigger consolidation started if not already done
          if (!this.activeChatIds.has(`${chatId}_consolidation_started`)) {
            this.activeChatIds.add(`${chatId}_consolidation_started`);
            this.triggerPersonaConsolidationStarted({
              ...chatEvent,
              message: "The first response may take a couple of minutes, but after that everything runs much faster."
            });
          }
          break;
          
        case 'processing':
          // If we were consolidating, mark it as complete
          if (this.activeChatIds.has(`${chatId}_consolidation_started`)) {
            this.triggerPersonaConsolidationCompleted(chatEvent);
            this.activeChatIds.delete(`${chatId}_consolidation_started`);
          }
          // Status is processing, response started was already triggered
          break;
          
        case 'completed':
          // Clean up any consolidation flags
          if (this.activeChatIds.has(`${chatId}_consolidation_started`)) {
            this.triggerPersonaConsolidationCompleted(chatEvent);
            this.activeChatIds.delete(`${chatId}_consolidation_started`);
          }
          this.triggerChatResponseCompleted(chatEvent);
          this.stopPollingForChat(chatId);
          break;
          
        case 'failed':
          // Clean up any consolidation flags
          if (this.activeChatIds.has(`${chatId}_consolidation_started`)) {
            this.triggerPersonaConsolidationCompleted(chatEvent);
            this.activeChatIds.delete(`${chatId}_consolidation_started`);
          }
          this.triggerChatResponseFailed(chatEvent);
          this.stopPollingForChat(chatId);
          break;
      }
    } catch (error: unknown) {
      console.error(`❌ Polling error for chat ${chatId}:`, error);
      // Stop polling on persistent errors
      this.stopPollingForChat(chatId);
      
      // Trigger failure callback
      const failureEvent: ChatEvent = {
        chat_id: chatId,
        chat_type: this.determineChatType(chatId),
        user_id: 0,
        error: error instanceof Error ? error.message : 'Polling failed',
        timestamp: new Date().toISOString(),
        tab_id: this.chatTabIds.get(chatId), // Include tab ID if available
      };
      this.triggerChatResponseFailed(failureEvent);
    }
  }

  /**
   * Determine chat type from chat_id format
   */
  private determineChatType(chatId: string): 'persona' | 'simulation' | 'group' {
    if (chatId.includes('simulation')) return 'simulation';
    if (chatId.includes('group')) return 'group';
    return 'persona';
  }

  /**
   * Start polling for a specific simulation
   */
  public startPollingForSimulation(simulationId: number): void {
    console.log(`🚀 startPollingForSimulation called - simulationId: ${simulationId}, usePolling: ${this.usePolling}`);
    if (!this.usePolling) {
      console.log(`⚠️ Not in polling mode, returning`);
      return;
    }
    
    // Avoid duplicate polling
    const simulationKey = `simulation_${simulationId}`;
    if (this.pollingIntervals.has(simulationKey)) {
      console.log(`⚠️ Already polling for simulation: ${simulationId}`);
      return;
    }
    
    console.log(`🔄 Starting polling for simulation: ${simulationId}`);
    this.activeSimulationIds.add(simulationId);
    
    // Immediately trigger "started" event to update UI
    const startedEvent: SimulationEvent = {
      simulation_id: simulationId,
      name: '',
      status: 'started',
      timestamp: new Date().toISOString(),
    };
    console.log(`📤 Triggering simulation started event:`, startedEvent);
    this.triggerSimulationStarted(startedEvent);
    
    // Poll immediately
    this.pollSimulationStatus(simulationId);
    
    // Set up interval for continued polling
    const interval = setInterval(() => {
      this.pollSimulationStatus(simulationId);
    }, 2000); // Poll every 2 seconds for simulation
    
    this.pollingIntervals.set(simulationKey, interval);
  }

  /**
   * Stop polling for a specific simulation
   */
  public stopPollingForSimulation(simulationId: number): void {
    if (!this.usePolling) return;
    
    const simulationKey = `simulation_${simulationId}`;
    const interval = this.pollingIntervals.get(simulationKey);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(simulationKey);
    }
    this.activeSimulationIds.delete(simulationId);
    console.log(`🛑 Stopped polling for simulation: ${simulationId}`);
  }

  /**
   * Poll the status of a specific simulation
   */
  private async pollSimulationStatus(simulationId: number): Promise<void> {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/simulations/${simulationId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to poll simulation status: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if simulation has completed (has simulation_response)
      const hasResponse = data.simulation_response && data.simulation_response.trim() !== '';
      const status = data.status || 'pending';
      
      // Determine simulation status
      if (hasResponse) {
        // Simulation completed successfully
        const completedEvent: SimulationEvent = {
          simulation_id: simulationId,
          name: data.name || '',
          status: 'completed',
          timestamp: new Date().toISOString(),
        };
        this.triggerSimulationCompleted(completedEvent);
        this.stopPollingForSimulation(simulationId);
      } else if (status === 'failed' || status === 'error') {
        // Simulation failed
        const failedEvent: SimulationEvent = {
          simulation_id: simulationId,
          name: data.name || '',
          status: 'failed',
          error: 'Simulation failed',
          timestamp: new Date().toISOString(),
        };
        this.triggerSimulationFailed(failedEvent);
        this.stopPollingForSimulation(simulationId);
      }
      // If still running, continue polling (no event triggered)
    } catch (error: unknown) {
      console.error(`❌ Polling error for simulation ${simulationId}:`, error);
      // Stop polling on persistent errors
      this.stopPollingForSimulation(simulationId);
      
      // Trigger failure callback
      const failureEvent: SimulationEvent = {
        simulation_id: simulationId,
        name: '',
        status: 'failed',
        error: error instanceof Error ? error.message : 'Polling failed',
        timestamp: new Date().toISOString(),
      };
      this.triggerSimulationFailed(failureEvent);
    }
  }

  /**
   * Trigger simulation started callbacks
   */
  private triggerSimulationStarted(data: SimulationEvent): void {
    console.log('📡 Polling: Simulation started:', data);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onSimulationStarted) {
      this.callbacks.onSimulationStarted(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onSimulationStarted) {
        callback.onSimulationStarted(data);
      }
    });
  }

  /**
   * Trigger simulation completed callbacks
   */
  private triggerSimulationCompleted(data: SimulationEvent): void {
    console.log('📡 Polling: Simulation completed:', data);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onSimulationCompleted) {
      this.callbacks.onSimulationCompleted(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onSimulationCompleted) {
        callback.onSimulationCompleted(data);
      }
    });
  }

  /**
   * Trigger simulation failed callbacks
   */
  private triggerSimulationFailed(data: SimulationEvent): void {
    console.error('❌ Polling: Simulation failed:', data);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onSimulationFailed) {
      this.callbacks.onSimulationFailed(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onSimulationFailed) {
        callback.onSimulationFailed(data);
      }
    });
  }

  /**
   * Start polling for question parsing
   */
  public startPollingForQuestionParsing(taskId: string): void {
    console.log(`🚀 startPollingForQuestionParsing called - taskId: ${taskId}, usePolling: ${this.usePolling}`);
    if (!this.usePolling) {
      console.log(`⚠️ Not in polling mode, returning`);
      return;
    }
    
    // Avoid duplicate polling
    const taskKey = `question_parsing_${taskId}`;
    if (this.pollingIntervals.has(taskKey)) {
      console.log(`⚠️ Already polling for question parsing: ${taskId}`);
      return;
    }
    
    console.log(`🔄 Starting polling for question parsing: ${taskId}`);
    this.activeQuestionParsingTaskIds.add(taskId);
    
    // Immediately trigger "started" event to update UI
    const startedEvent: QuestionParsingEvent = {
      task_id: taskId,
      user_id: 0,
      status: 'started',
      progress: 0,
      current_step: 'Starting question parsing...',
      timestamp: new Date().toISOString(),
    };
    console.log(`📤 Triggering question parsing started event:`, startedEvent);
    this.triggerQuestionParsingStarted(startedEvent);
    
    // Poll immediately
    this.pollQuestionParsingStatus(taskId);
    
    // Set up interval for continued polling
    const interval = setInterval(() => {
      this.pollQuestionParsingStatus(taskId);
    }, 1500); // Poll every 1.5 seconds for question parsing
    
    this.pollingIntervals.set(taskKey, interval);
  }

  /**
   * Stop polling for question parsing
   */
  public stopPollingForQuestionParsing(taskId: string): void {
    if (!this.usePolling) return;
    
    const taskKey = `question_parsing_${taskId}`;
    const interval = this.pollingIntervals.get(taskKey);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(taskKey);
    }
    this.activeQuestionParsingTaskIds.delete(taskId);
    console.log(`🛑 Stopped polling for question parsing: ${taskId}`);
  }

  /**
   * Poll the status of question parsing
   */
  private async pollQuestionParsingStatus(taskId: string): Promise<void> {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/task/status/${taskId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to poll question parsing status: ${response.status}`);
      }

      const data = await response.json();
      
      // Convert task status to question parsing event format
      const status = data.status || 'pending';
      const progress = data.progress || 0;
      
      // Determine question parsing status
      if (status === 'completed' && data.result) {
        // Question parsing completed successfully
        const completedEvent: QuestionParsingEvent = {
          task_id: taskId,
          user_id: 0,
          status: 'completed',
          progress: 100,
          parsed_questions: data.result.parsed_questions || data.result.questions || [],
          questions_count: data.result.questions_count || (data.result.parsed_questions?.length || 0),
          timestamp: data.timestamp || new Date().toISOString(),
        };
        this.triggerQuestionParsingCompleted(completedEvent);
        this.stopPollingForQuestionParsing(taskId);
      } else if (status === 'failed') {
        // Question parsing failed
        const failedEvent: QuestionParsingEvent = {
          task_id: taskId,
          user_id: 0,
          status: 'failed',
          error: data.error || data.detail || 'Question parsing failed',
          timestamp: data.timestamp || new Date().toISOString(),
        };
        this.triggerQuestionParsingFailed(failedEvent);
        this.stopPollingForQuestionParsing(taskId);
      } else if (status === 'processing' || status === 'pending') {
        // Still processing, update progress
        const progressEvent: QuestionParsingEvent = {
          task_id: taskId,
          user_id: 0,
          status: 'progress',
          progress: progress,
          current_step: data.result?.current_step || 'Processing questions...',
          timestamp: data.timestamp || new Date().toISOString(),
        };
        this.triggerQuestionParsingProgress(progressEvent);
      }
      // If still pending, continue polling (no event triggered)
    } catch (error: unknown) {
      console.error(`❌ Polling error for question parsing ${taskId}:`, error);
      // Stop polling on persistent errors
      this.stopPollingForQuestionParsing(taskId);
      
      // Trigger failure callback
      const failureEvent: QuestionParsingEvent = {
        task_id: taskId,
        user_id: 0,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Polling failed',
        timestamp: new Date().toISOString(),
      };
      this.triggerQuestionParsingFailed(failureEvent);
    }
  }

  /**
   * Trigger question parsing started callbacks
   */
  private triggerQuestionParsingStarted(data: QuestionParsingEvent): void {
    console.log('📡 Polling: Question parsing started:', data);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onQuestionParsingStarted) {
      this.callbacks.onQuestionParsingStarted(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onQuestionParsingStarted) {
        callback.onQuestionParsingStarted(data);
      }
    });
  }

  /**
   * Trigger question parsing progress callbacks
   */
  private triggerQuestionParsingProgress(data: QuestionParsingEvent): void {
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onQuestionParsingProgress) {
      this.callbacks.onQuestionParsingProgress(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onQuestionParsingProgress) {
        callback.onQuestionParsingProgress(data);
      }
    });
  }

  /**
   * Trigger question parsing completed callbacks
   */
  private triggerQuestionParsingCompleted(data: QuestionParsingEvent): void {
    console.log('📡 Polling: Question parsing completed:', data);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onQuestionParsingCompleted) {
      this.callbacks.onQuestionParsingCompleted(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onQuestionParsingCompleted) {
        callback.onQuestionParsingCompleted(data);
      }
    });
  }

  /**
   * Trigger question parsing failed callbacks
   */
  private triggerQuestionParsingFailed(data: QuestionParsingEvent): void {
    console.error('❌ Polling: Question parsing failed:', data);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onQuestionParsingFailed) {
      this.callbacks.onQuestionParsingFailed(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onQuestionParsingFailed) {
        callback.onQuestionParsingFailed(data);
      }
    });
  }

  /**
   * Trigger chat response started callbacks
   */
  private triggerChatResponseStarted(data: ChatEvent): void {
    console.log('📡 Polling: Chat response started:', data);
    console.log(`📊 Number of subscribers: ${this.subscriberCallbacks.size}`);
    console.log(`📊 Legacy callbacks registered: ${!!this.callbacks.onChatResponseStarted}`);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onChatResponseStarted) {
      console.log('🔔 Calling legacy onChatResponseStarted callback');
      this.callbacks.onChatResponseStarted(data);
    }
    
    // Call all subscriber callbacks
    let callbackCount = 0;
    this.subscriberCallbacks.forEach((callback, subscriberId) => {
      if (callback.onChatResponseStarted) {
        console.log(`🔔 Calling subscriber ${subscriberId} onChatResponseStarted`);
        callback.onChatResponseStarted(data);
        callbackCount++;
      }
    });
    console.log(`📊 Called ${callbackCount} subscriber callbacks`);
  }

  /**
   * Trigger chat response completed callbacks
   */
  private triggerChatResponseCompleted(data: ChatEvent): void {
    console.log('📡 Polling: Chat response completed:', data);
    console.log(`📊 Number of subscribers: ${this.subscriberCallbacks.size}`);
    console.log(`📊 Legacy callbacks registered: ${!!this.callbacks.onChatResponseCompleted}`);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onChatResponseCompleted) {
      console.log('🔔 Calling legacy onChatResponseCompleted callback');
      this.callbacks.onChatResponseCompleted(data);
    }
    
    // Call all subscriber callbacks
    let callbackCount = 0;
    this.subscriberCallbacks.forEach((callback, subscriberId) => {
      if (callback.onChatResponseCompleted) {
        console.log(`🔔 Calling subscriber ${subscriberId} onChatResponseCompleted`);
        callback.onChatResponseCompleted(data);
        callbackCount++;
      }
    });
    console.log(`📊 Called ${callbackCount} subscriber callbacks`);
  }

  /**
   * Trigger chat response failed callbacks
   */
  private triggerChatResponseFailed(data: ChatEvent): void {
    console.error('❌ Polling: Chat response failed:', data);
    
    // Call legacy callback for backwards compatibility
    this.callbacks.onChatResponseFailed?.(data);
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onChatResponseFailed) {
        callback.onChatResponseFailed(data);
      }
    });
  }

  /**
   * Trigger persona consolidation started callbacks
   */
  private triggerPersonaConsolidationStarted(data: ChatEvent): void {
    console.log('📡 Polling: Persona consolidation started:', data);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onPersonaConsolidationStarted) {
      this.callbacks.onPersonaConsolidationStarted(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onPersonaConsolidationStarted) {
        callback.onPersonaConsolidationStarted(data);
      }
    });
  }

  /**
   * Trigger persona consolidation completed callbacks
   */
  private triggerPersonaConsolidationCompleted(data: ChatEvent): void {
    console.log('📡 Polling: Persona consolidation completed:', data);
    
    // Call legacy callback for backwards compatibility
    if (this.callbacks.onPersonaConsolidationCompleted) {
      this.callbacks.onPersonaConsolidationCompleted(data);
    }
    
    // Call all subscriber callbacks
    this.subscriberCallbacks.forEach((callback) => {
      if (callback.onPersonaConsolidationCompleted) {
        callback.onPersonaConsolidationCompleted(data);
      }
    });
  }

  /**
   * Setup WebSocket event listeners (only used when not polling)
   */
  private setupEventListeners(): void {
    if (!this.socket || this.usePolling) return;

    // Simulation events
    this.socket.on('simulation_started', (data: SimulationEvent) => {
      console.log('📡 SocketService: Received simulation_started:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onSimulationStarted?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onSimulationStarted) {
          callback.onSimulationStarted(data);
        }
      });
    });

    this.socket.on('simulation_completed', (data: SimulationEvent) => {
      console.log('📡 SocketService: Received simulation_completed:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onSimulationCompleted?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onSimulationCompleted) {
          callback.onSimulationCompleted(data);
        }
      });
    });

    this.socket.on('simulation_failed', (data: SimulationEvent) => {
      console.error('❌ SocketService: Received simulation_failed:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onSimulationFailed?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onSimulationFailed) {
          callback.onSimulationFailed(data);
        }
      });
    });

    // Chat events
    this.socket.on('chat_message_received', (data: ChatEvent) => {
      console.log('📡 SocketService: Received chat_message_received:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onChatMessageReceived?.(data);
      
      // Call all subscriber callbacks that have this specific callback
      this.subscriberCallbacks.forEach((callback, subscriberId) => {
        if (callback.onChatMessageReceived) {
          try {
            callback.onChatMessageReceived(data);
          } catch (error) {
            console.error(`📡 SocketService: Error calling subscriber ${subscriberId} callback:`, error);
          }
        }
      });
    });

    this.socket.on('chat_response_started', (data: ChatEvent) => {
      console.log('📡 SocketService: Received chat_response_started:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onChatResponseStarted?.(data);
      
      // Call all subscriber callbacks that have this specific callback
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onChatResponseStarted) {
          callback.onChatResponseStarted(data);
        }
      });
    });

    this.socket.on('chat_response_completed', (data: ChatEvent) => {
      console.log('📡 SocketService: Received chat_response_completed:', data);
      // Call legacy callback for backwards compatibility
      this.callbacks.onChatResponseCompleted?.(data);
      
      // Call all subscriber callbacks that have this specific callback
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onChatResponseCompleted) {
          callback.onChatResponseCompleted(data);
        }
      });
    });

    this.socket.on('chat_response_failed', (data: ChatEvent) => {
      console.error('❌ SocketService: Received chat_response_failed:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onChatResponseFailed?.(data);
      
      // Call all subscriber callbacks that have this specific callback
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onChatResponseFailed) {
          callback.onChatResponseFailed(data);
        }
      });
    });

    // Persona consolidation events
    this.socket.on('persona_consolidation_started', (data: ChatEvent) => {
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onPersonaConsolidationStarted?.(data);
      
      // Call all subscriber callbacks that have this specific callback
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onPersonaConsolidationStarted) {
          callback.onPersonaConsolidationStarted(data);
        }
      });
    });

    this.socket.on('persona_consolidation_completed', (data: ChatEvent) => {
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onPersonaConsolidationCompleted?.(data);
      
      // Call all subscriber callbacks that have this specific callback
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onPersonaConsolidationCompleted) {
          callback.onPersonaConsolidationCompleted(data);
        }
      });
    });

    // PDF events
    this.socket.on('pdf_generation_started', (data: PdfEvent) => {
      console.log('📡 SocketService: Received pdf_generation_started:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onPdfGenerationStarted?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onPdfGenerationStarted) {
          callback.onPdfGenerationStarted(data);
        }
      });
    });

    this.socket.on('pdf_generation_completed', (data: PdfEvent) => {
      console.log('📡 SocketService: Received pdf_generation_completed:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onPdfGenerationCompleted?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onPdfGenerationCompleted) {
          callback.onPdfGenerationCompleted(data);
        }
      });
    });

    this.socket.on('pdf_generation_failed', (data: PdfEvent) => {
      console.error('❌ SocketService: Received pdf_generation_failed:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onPdfGenerationFailed?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onPdfGenerationFailed) {
          callback.onPdfGenerationFailed(data);
        }
      });
    });

    // Question parsing events
    this.socket.on('question_parsing_started', (data: QuestionParsingEvent) => {
      console.log('📡 SocketService: Received question_parsing_started:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onQuestionParsingStarted?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onQuestionParsingStarted) {
          callback.onQuestionParsingStarted(data);
        }
      });
    });

    this.socket.on('question_parsing_progress', (data: QuestionParsingEvent) => {
      // Call legacy callback for backwards compatibility
      this.callbacks.onQuestionParsingProgress?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onQuestionParsingProgress) {
          callback.onQuestionParsingProgress(data);
        }
      });
    });

    this.socket.on('question_parsing_completed', (data: QuestionParsingEvent) => {
      console.log('📡 SocketService: Received question_parsing_completed:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onQuestionParsingCompleted?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onQuestionParsingCompleted) {
          callback.onQuestionParsingCompleted(data);
        }
      });
    });

    this.socket.on('question_parsing_failed', (data: QuestionParsingEvent) => {
      console.error('❌ SocketService: Received question_parsing_failed:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onQuestionParsingFailed?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onQuestionParsingFailed) {
          callback.onQuestionParsingFailed(data);
        }
      });
    });

    this.socket.on('question_parsing_cancelled', (data: QuestionParsingEvent) => {
      console.log('📡 SocketService: Received question_parsing_cancelled:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onQuestionParsingCancelled?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onQuestionParsingCancelled) {
          callback.onQuestionParsingCancelled(data);
        }
      });
    });

    // Usage update events
    this.socket.on('usage_updated', (data: UsageUpdateEvent) => {
      console.log('📡 SocketService: Received usage_updated:', data);
      
      // Call legacy callback for backwards compatibility
      this.callbacks.onUsageUpdated?.(data);
      
      // Call all subscriber callbacks
      this.subscriberCallbacks.forEach((callback) => {
        if (callback.onUsageUpdated) {
          callback.onUsageUpdated(data);
        }
      });
    });
  }

  /**
   * Subscribe to simulation events (supports multiple subscribers)
   */
  public subscribeToEvents(callbacks: EventCallbacks): string {
    // Generate unique subscriber ID
    const subscriberId = `subscriber_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Only add to legacy callbacks if they don't already exist (prevent overwriting)
    // This maintains backwards compatibility while preventing callback conflicts
    Object.keys(callbacks).forEach(key => {
      const callbackKey = key as keyof EventCallbacks;
      if (!this.callbacks[callbackKey] && callbacks[callbackKey]) {
        (this.callbacks as any)[callbackKey] = callbacks[callbackKey];
      }
    });
    
    // Add to multi-subscriber Map (this is the primary mechanism)
    this.subscriberCallbacks.set(subscriberId, callbacks);
    
    return subscriberId;
  }

  /**
   * Subscribe to a specific simulation
   */
  public subscribeToSimulation(simulationId: number): void {
    if (this.usePolling) {
      // Start polling for this simulation
      this.startPollingForSimulation(simulationId);
    } else if (this.socket?.connected) {
      this.socket.emit('subscribe_simulation', { simulation_id: simulationId });
    }
  }

  /**
   * Handle chat submission response (for polling mode)
   */
  public handleChatSubmitted(chatId: string, tabId?: string): void {
    console.log(`🔍 handleChatSubmitted called - chatId: ${chatId}, tabId: ${tabId}, usePolling: ${this.usePolling}`);
    if (this.usePolling && chatId) {
      console.log(`✅ Starting polling for chat: ${chatId} with tabId: ${tabId}`);
      // Start polling for this chat
      this.startPollingForChat(chatId, tabId);
    } else if (!this.usePolling) {
      console.log(`⚠️ Not in polling mode, WebSocket should handle updates`);
    } else {
      console.log(`❌ No chat ID provided`);
    }
  }

  /**
   * Unsubscribe from events
   */
  public unsubscribeFromEvents(): void {
    this.callbacks = {};
    this.subscriberCallbacks.clear();
    
    // Stop all polling if in polling mode
    if (this.usePolling) {
      this.pollingIntervals.forEach(interval => clearInterval(interval));
      this.pollingIntervals.clear();
      this.activeChatIds.clear();
      this.activeSimulationIds.clear();
      this.activeQuestionParsingTaskIds.clear();
    }
  }
  
  /**
   * Unsubscribe a specific subscriber
   */
  public unsubscribeSubscriber(subscriberId: string): void {
    this.subscriberCallbacks.delete(subscriberId);
  }

  /**
   * Check if socket is connected (or polling is active)
   */
  public isConnected(): boolean {
    if (this.usePolling) {
      return true; // Always "connected" in polling mode
    }
    return this.socket?.connected || false;
  }

  /**
   * Disconnect from WebSocket server (or stop polling)
   */
  public disconnect(): void {
    // Stop any pending reconnection attempts
    this.stopReconnection();
    
    // Stop all polling intervals
    if (this.usePolling) {
      this.pollingIntervals.forEach(interval => clearInterval(interval));
      this.pollingIntervals.clear();
      this.activeChatIds.clear();
      this.activeSimulationIds.clear();
      this.activeQuestionParsingTaskIds.clear();
    }
    
    // Disconnect socket if using WebSocket mode
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.callbacks = {};
    this.isConnecting = false;
  }

  /**
   * Get connection status
   */
  public getConnectionStatus(): 'connected' | 'connecting' | 'disconnected' {
    if (this.usePolling) {
      return 'connected'; // Always "connected" in polling mode
    }
    if (this.socket?.connected) return 'connected';
    if (this.isConnecting) return 'connecting';
    return 'disconnected';
  }

  /**
   * Check if using polling mode
   */
  public isPollingMode(): boolean {
    return this.usePolling;
  }
}

// Export types
export type { SimulationEvent, ChatEvent, PdfEvent, QuestionParsingEvent, UsageUpdateEvent };

// Export singleton instance
export const socketService = SocketService.getInstance();
export default socketService;